/**
 * NativeAuthButtons
 *
 * Renders native Sign in with Apple + Google sign-in buttons on iOS/Android
 * builds only. Returns null on the web build so the existing Emergent Google
 * OAuth flow keeps its exact placement. Do NOT redesign — this component
 * follows Apple's Human Interface Guidelines (Apple button first on iOS,
 * black button, white glyph, "Continue with Apple" label) and matches the
 * FutureSelf pill-button aesthetic.
 */

import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  isAppleSignInSupported,
  isNativeGoogleSignInSupported,
  getAppleNativeCredential,
  getGoogleNativeCredential,
} from '../lib/nativeAuth';
import { isNativePlatform } from '../lib/native';
import { hapticLight } from '../lib/nativeFeel';
import { useProviderSignIn } from '../hooks/useProviderSignIn';
import AccountChoiceDialog from './AccountChoiceDialog';

const AppleGlyph = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M17.564 12.658c-.021-2.198 1.798-3.256 1.881-3.308-1.026-1.5-2.62-1.706-3.19-1.73-1.358-.137-2.653.8-3.343.8-.69 0-1.756-.78-2.888-.759-1.486.022-2.856.86-3.62 2.184-1.542 2.668-.394 6.617 1.108 8.784.734 1.056 1.61 2.24 2.762 2.199 1.108-.045 1.527-.717 2.866-.717 1.338 0 1.716.717 2.888.696 1.19-.022 1.945-1.072 2.675-2.132.842-1.222 1.19-2.407 1.212-2.468-.026-.012-2.325-.892-2.351-3.549zM15.383 5.86c.612-.741 1.023-1.769.911-2.797-.881.035-1.947.586-2.579 1.327-.567.657-1.062 1.706-.929 2.713.982.076 1.985-.5 2.597-1.243z"
    />
  </svg>
);

const GoogleGlyph = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#8BAA9E" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#B8AFA6" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#C6A75E" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#A8A8A8" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const NativeAuthButtons = ({ redirectTo = '/dashboard' }) => {
  const [loading, setLoading] = useState(null); // 'apple' | 'google' | null
  const { signIn, pending, busy, confirmCreate, chooseExisting } = useProviderSignIn(redirectTo);

  // WEB: render nothing; WebProviderButtons handles the website.
  if (!isNativePlatform()) return null;

  const run = async (provider, getCredential) => {
    setLoading(provider);
    hapticLight();
    await signIn(provider, getCredential);
    setLoading(null);
  };
  const handleApple = () => run('apple', () => getAppleNativeCredential());
  const handleGoogle = () => run('google', () => getGoogleNativeCredential());

  const showApple = isAppleSignInSupported();
  const showGoogle = isNativeGoogleSignInSupported();
  if (!showApple && !showGoogle) return null;

  return (
    <div className="w-full space-y-3 mb-6" data-testid="native-auth-buttons">
      {/* Apple must appear FIRST when offered alongside Google, per HIG. */}
      {showApple && (
        <Button
          type="button"
          onClick={handleApple}
          disabled={loading !== null}
          className="w-full bg-black text-white hover:bg-[#111] rounded-2xl py-6 flex items-center justify-center gap-3 border border-white/10"
          data-testid="apple-signin-btn"
        >
          {loading === 'apple' ? (
            <div className="w-5 h-5 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <AppleGlyph />
              <span className="font-sans font-medium">Continue with Apple</span>
            </>
          )}
        </Button>
      )}

      {showGoogle && (
        <Button
          type="button"
          variant="outline"
          onClick={handleGoogle}
          disabled={loading !== null}
          className="w-full border-[#B8AFA6]/30 text-[#DCD3CA] hover:bg-[#B8AFA6]/10 rounded-2xl py-6 flex items-center justify-center gap-3"
          data-testid="google-native-signin-btn"
        >
          {loading === 'google' ? (
            <div className="w-5 h-5 border-2 border-[#8BAA9E] border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <GoogleGlyph />
              <span className="font-sans font-medium">Continue with Google</span>
            </>
          )}
        </Button>
      )}

      <div className="flex items-center gap-4 pt-1">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-[#7A7670] text-sm font-sans">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <AccountChoiceDialog
        provider={pending?.provider}
        open={!!pending}
        busy={busy}
        onCreate={confirmCreate}
        onExisting={chooseExisting}
      />
    </div>
  );
};

// Named export for use inside auth pages. Default kept for future flexibility.
export { NativeAuthButtons };
export default NativeAuthButtons;
